import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { LoggerService } from '@shared/services/logger.service';
import { NativeFileService } from '@shared/services/native-file.service';
import JSZip from 'jszip';
import { FileHelper } from '@core/helpers/file.helper';
import { SupportPackage } from '../models/support-package';
import { SupportFiles } from '../models/support-files';

@Injectable({ providedIn: 'root' })
export class SyncBackupService {
  #nativeFileService = inject(NativeFileService);
  #logger = inject(LoggerService);

  public async init(): Promise<void> {
    this.#logger.info('Creating sync directory if it does not exist.');
    try {
      await this.#nativeFileService.createFolderIfNotExist({ path: 'sync' });
      this.#logger.info('Sync directory created successfully.');
    } catch (error) {
      this.#logger.error('Failed to create sync directory.', error);
    }
  }

  public async backup(taskId: string, request: FormData): Promise<void> {
    this.#logger.info(`Backing up sync request for task ${taskId}.`);
    const zip = new JSZip();
    const hash = await this.getBackupId(request);
    request.forEach((value, key) => zip.file(key, value));
    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 9 } }, info => {
      this.#logger.info(`Compressing sync request backup for task ${taskId}: ${info.percent.toFixed(2)}%`);
    });
    const base64Zip = await FileHelper.blobToBase64Split(zipBlob);
    await this.#nativeFileService.write({ path: `sync/${taskId}/${hash}.zip`, data: base64Zip });
    this.#logger.info(`Sync request for task ${taskId} backed up successfully.`);
  }

  public async removeBackup(taskId: string, backupId: string): Promise<boolean> {
    this.#logger.info(`Removing sync backup for task ${taskId} with id ${backupId}`);
    try {
      await this.#nativeFileService.remove({ path: `sync/${taskId}/${backupId}.zip` });
    } catch (error) {
      this.#logger.error(`Failed to remove sync backup for task ${taskId} with id ${backupId}`, error);
      return false;
    }
    return true;
  }

  public async removeRequestBackup(taskId: string, request: FormData): Promise<boolean> {
    const backupId = await this.getBackupId(request);
    return await this.removeBackup(taskId, backupId);
  }

  public async getSupportPackages(): Promise<SupportPackage[]> {
    const results: SupportPackage[] = [];
    const { files: syncFiles } = await this.#nativeFileService.readDirectory({ path: 'sync' });
    const taskIds = syncFiles.filter(file => file.type === 'directory').map(dir => dir.name);
    const retentionPeriod = 1000 * 60 * 60 * 24 * 21; // 21 days
    for (const taskId of taskIds) {
      const { files } = await this.#nativeFileService.readDirectory({ path: `sync/${taskId}` });
      for (const file of files) {
        if (file.type !== 'file' || !file.name.endsWith('.zip')) {
          continue;
        }
        results.push({
          id: file.name.substring(0, file.name.length - '.zip'.length),
          taskId,
          uri: await this.#nativeFileService.getFullFileUri(`sync/${taskId}/${file.name}`),
          timestamp: file.mtime,
          expiration: file.mtime + retentionPeriod,
        });
      }
    }
    return results;
  }

  public async getTaskSupportFiles(taskId: string): Promise<SupportFiles[]> {
    const results: SupportFiles[] = [];
    try {
      const { files } = await this.#nativeFileService.readDirectory({ path: taskId });

      for (const file of files) {
        if (file.type !== 'file' || file.name.endsWith('.zip')) {
          continue;
        }
        results.push({
          id: file.name,
          taskId,
          uri: file.uri,
        });
      }
    } catch (error) {
      this.#logger.error(`No files directory found for task ${taskId}`, error);
    }

    return results;
  }

  public async cleanupExpiredSupportPackages({ excludedTaskIds }: { excludedTaskIds: string[] }): Promise<void> {
    this.#logger.info(`Cleaning up support packages. Excluding:`, excludedTaskIds);
    const supportPackages = await this.getSupportPackages();
    const now = Date.now();
    for (const pkg of supportPackages) {
      if (excludedTaskIds.includes(pkg.taskId)) {
        continue;
      }
      if (now < pkg.expiration) {
        this.#logger.info(`Skipping support package for task ${pkg.taskId} with id ${pkg.id} as it has not exceeded retention period.`);
        continue;
      }
      this.#logger.info(`Removing support package for task ${pkg.taskId} with id ${pkg.id}`);
      try {
        await this.#nativeFileService.remove({ path: `sync/${pkg.taskId}/${pkg.id}.zip` });
      } catch (error) {
        this.#logger.error(`Failed to remove support package for task ${pkg.taskId} with id ${pkg.id}`, error);
      }
    }
  }

  private async getBackupId(request: FormData): Promise<string> {
    const body = request.get('body');
    if (!body) {
      throw new Error('No body found in FormData');
    }
    const encoder = new TextEncoder();
    const bodyBuffer = encoder.encode(body as string);
    const hashBuffer = environment.production
      ? await crypto.subtle.digest('SHA-256', bodyBuffer) // crypto is only available in secure context (HTTPS).
      : bodyBuffer.slice(bodyBuffer.byteLength - 32, bodyBuffer.byteLength); // For development, use the last 32 bytes as a simple hash

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }
}
